import {
  ref,
  computed,
  onUnmounted,
  type MaybeRefOrGetter,
  type ComputedRef,
  toValue,
} from "vue"

type Breakpoint = "sm" | "md" | "lg" | "xl" | "2xl"
export type ResponsiveProp<T> = { default?: T } & Partial<Record<Breakpoint, T>>

const BREAKPOINTS: Record<Breakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
}

const BP_ORDER: Breakpoint[] = ["2xl", "xl", "lg", "md", "sm"]

export function useResponsiveProp<T>(
  prop: MaybeRefOrGetter<T | ResponsiveProp<T> | undefined>,
): ComputedRef<T | undefined> {
  if (typeof window === "undefined") {
    return computed(() => {
      const v = toValue(prop)
      if (v === undefined || v === null) return undefined
      if (typeof v === "object" && !Array.isArray(v))
        return (v as ResponsiveProp<T>).default
      return v as T
    })
  }

  const active = ref<Set<Breakpoint>>(new Set())
  const queries: Array<{ mq: MediaQueryList; handler: () => void }> = []

  for (const [key, px] of Object.entries(BREAKPOINTS) as [
    Breakpoint,
    number,
  ][]) {
    const mq = window.matchMedia(`(min-width: ${px}px)`)
    const handler = () => {
      const next = new Set(active.value)
      if (mq.matches) next.add(key)
      else next.delete(key)
      active.value = next
    }
    if (mq.matches) active.value.add(key)
    mq.addEventListener("change", handler)
    queries.push({ mq, handler })
  }

  onUnmounted(() => {
    for (const { mq, handler } of queries)
      mq.removeEventListener("change", handler)
  })

  return computed(() => {
    const v = toValue(prop)
    if (v === undefined || v === null) return undefined
    if (typeof v !== "object" || Array.isArray(v)) return v as T

    const responsive = v as ResponsiveProp<T>
    for (const bp of BP_ORDER) {
      if (active.value.has(bp) && responsive[bp] !== undefined)
        return responsive[bp] as T
    }
    return responsive.default
  })
}
